import english from './messages/en.json';
import protectedTerms from './protected-terms.json';
import registry from './locales.json';
import { catalogLoaders } from './catalog-loaders.mjs';
import { createTranslator, validateCatalog, matchLocale, resolveLocale, svgMessageKey, slotName, translatableAttributes, groupInlineArguments } from './core.mjs';
import { applyLocaleAttributes, directionOverride } from './direction.mjs';
export { getDirection, isRTL } from './direction.mjs';

export const languages = Object.freeze(registry.languages.map(language => Object.freeze({ ...language })));
export const defaultLocale = registry.defaultLocale;
export const storageKey = registry.storageKey;
// Register a reviewed catalogue here before adding its locale to the public registry.
const catalogs = new Map([[defaultLocale, english]]);
let currentLocale = defaultLocale;
// Generated pages include only their bound and runtime messages. Seed the
// formatter before controllers initialize, with no translation network round trip.
try {
  const state = JSON.parse(document.getElementById('an-i18n-state')?.textContent ?? 'null');
  if (state && languages.some(language => language.code === state.locale) && state.locale !== defaultLocale) {
    const reference = Object.fromEntries(Object.keys(state.messages).map(key => [key, english[key]]));
    validateCatalog(state.locale, state.messages, reference, protectedTerms);
    catalogs.set(state.locale, state.messages);
    currentLocale = state.locale;
  }
} catch { /* Legacy pages retain the ordinary validated catalogue loader. */ }
let translator = createTranslator({ locale: currentLocale, messages: catalogs.get(currentLocale), fallback: english, protectedTerms });
let requestVersion = 0;
const templates = new WeakMap();

export const getLocale = () => currentLocale;
export const t = (key, values) => translator.text(key, values);
export const formatNumber = (value, options) => translator.number(value, options);
export const formatCurrency = (value, currency, options) => translator.currency(value, currency, options);
export const formatPercent = (value, options) => translator.percent(value, options);
export const formatDate = (value, options) => translator.date(value, options);
export const formatRelativeTime = (value, unit, options) => translator.relativeTime(value, unit, options);
export const formatList = (values, options) => translator.list(values, options);
export const supportedLocale = value => matchLocale(value, languages);
export const selectLocale = options => resolveLocale({ ...options, languages, defaultLocale });

function bindingKey(element) {
  return element.dataset.i18n ?? svgMessageKey(element.getAttribute('class') ?? '');
}

export function applyTranslations(root = document) {
  function visit(element) {
    const values = JSON.parse(element.dataset.i18nValues ?? '{}');
    if (element.hasAttribute('data-language-trigger')) values.language = languages.find(value => value.code === currentLocale).nativeLabel;
    const attributes = JSON.parse(element.dataset.i18nAttrs ?? '{}');
    for (const [attribute, key] of Object.entries(attributes)) {
      if (translatableAttributes.includes(attribute)) element.setAttribute(attribute, t(key, values));
    }
    const key = bindingKey(element);
    if (key) {
      if (!templates.has(element)) templates.set(element, element.cloneNode(true));
      const template = templates.get(element);
      const liveSlots = new Map([...element.querySelectorAll('[data-i18n-slot],[class*="an-i18n-slot--"]')].map(child => [child.dataset.i18nSlot ?? slotName(child.getAttribute('class') ?? ''), child]));
      const usedSlots = new Set();
      const argumentsCreated = new WeakSet();
      const inserted = new Set();
      const toNodes = parts => parts.map(part => {
        if (typeof part !== 'object') return document.createTextNode(String(part));
        // Repeated ICU arguments need distinct DOM nodes at each occurrence.
        const node = inserted.has(part) ? part.cloneNode(true) : part;
        inserted.add(part);
        return node;
      });
      function nodes(parts) {
        return toNodes(groupInlineArguments(parts, part => typeof part === 'object' && argumentsCreated.has(part), chunks => {
          const span = document.createElement('span');
          span.replaceChildren(...toNodes(chunks));
          return span;
        }));
      }
      const slots = {};
      for (const child of template.querySelectorAll('[data-i18n-slot],[class*="an-i18n-slot--"]')) {
        const name = child.dataset.i18nSlot ?? slotName(child.getAttribute('class') ?? '');
        slots[name] = chunks => {
          if (usedSlots.has(name)) throw new Error(`Repeated rich slot: ${key}.${name}`);
          usedSlots.add(name);
          const clone = liveSlots.get(name) ?? child.cloneNode(true);
          if (child.getAttribute('translate') !== 'no' && !['svg', 'img', 'br', 'wbr'].includes(child.tagName.toLowerCase())) {
            clone.replaceChildren(...nodes(chunks));
          }
          return clone;
        };
      }
      const html = element.namespaceURI !== 'http://www.w3.org/2000/svg' && !['TITLE','TEXTAREA'].includes(element.tagName);
      const result = html ? translator.formatMarkup(key, { ...values, ...slots }, (value, info) => {
        const isolated = document.createElement('bdi');
        isolated.dir = info.protected ? 'ltr' : 'auto';
        isolated.textContent = String(value);
        argumentsCreated.add(isolated);
        return isolated;
      }) : translator.format(key, { ...values, ...slots });
      const parts = Array.isArray(result) ? result : [result];
      // Reuse existing slots to preserve their listeners and state. Catalogue text stays inert.
      element.replaceChildren(...nodes(parts));
    }
    // Visit the live children after rendering rich messages, including their attribute bindings.
    for (const child of element.children) visit(child);
  }
  visit(root.documentElement ?? root);
}

export async function setLocale(requested, { refresh = false } = {}) {
  const version = ++requestVersion;
  let locale = supportedLocale(requested) ?? defaultLocale;
  if (!catalogs.has(locale)) {
    try {
      const messages = await catalogLoaders[locale]?.();
      validateCatalog(locale, messages, english, protectedTerms);
      catalogs.set(locale, messages);
    } catch { locale = defaultLocale; }
  }
  if (version !== requestVersion) return currentLocale;
  const changed = currentLocale !== locale;
  currentLocale = locale;
  translator = createTranslator({ locale, messages: catalogs.get(locale), fallback: english, protectedTerms });
  // Parcel removes this development branch from production. Direction previews
  // retain the real content language and never publish an untranslated locale.
  const override = process.env.NODE_ENV !== 'production' ? directionOverride(location.search) : undefined;
  const attributes = applyLocaleAttributes(document.documentElement, locale, override);
  if (changed || refresh) applyTranslations();
  window.dispatchEvent(new CustomEvent('an:languagechange', { detail: { locale, direction: attributes.dir, script: attributes['data-script'] } }));
  return locale;
}
